<template>
  <div class="relative w-[200px]">
    <MultiSelect
      ref="multiSelectRef"
      v-model="model"
      show-clear
      display="chip"
      class="max-h-8 min-w-20 rounded-xl text-center"
      inputClass="h-8 self-center min-w-20"
      :option-label="optionLabel"
      :option-value="optionValue"
      :options="options"
      :pt="computedPt"
      @blur="emits('blur', $event)"
      @change="emits('change', $event)"
    >
      <template #dropdownicon>
        <img
          src="@/assets/svg-icons/SortDown.svg"
          class="w-2 h-2"
          :class="{ 'opacity-40': loading }"
        />
      </template>

      <template #removetokenicon="{ removeCallback, item }">
        <img
          src="@/assets/svg-icons/Close.svg"
          class="w-2 h-2 relative left-[-5px] cursor-pointer"
          @mousedown.prevent
          @click.stop.prevent="removeCallback($event, item)"
        />
      </template>
    </MultiSelect>
  </div>
</template>

<script setup lang="ts">
import MultiSelect from 'primevue/multiselect'
import { defineModel, nextTick, ref, watch, computed } from 'vue'

const props = defineProps<{
  options: any[]
  optionLabel?: string
  optionValue?: string
  placeholder?: string
  loading?: boolean
}>()

const emits = defineEmits<{
  blur: [Event]
  change: [any]
}>()

const model = defineModel<any[] | null>()
const multiSelectRef = ref<any>(null)

function isOverlayReady(inst: any) {
  if (!inst) return false
  const el = inst.overlay || inst.overlayRef?.value || inst.$refs?.overlay
  const visible = inst.overlayVisible ?? !!el
  return !!(el && (visible || el?.style))
}

watch(model, async () => {
  await nextTick()
  realignOverlay()
})

function realignOverlay() {
  const inst = multiSelectRef.value
  if (!inst || typeof inst.alignOverlay !== 'function') return
  if (!isOverlayReady(inst)) return
  try {
    inst.alignOverlay()
  } catch {
    // панель могла исчезнуть между проверкой и вызовом
  }
}

// Динамическое вычисление pt с учётом состояния loading
const computedPt = computed(() => ({
  root: {
    class: [
      'relative w-[200px] overflow-hidden shadow-none focus:border-[#BDBDBD]',
      props.loading ? 'pointer-events-none !bg-gray' : ''
    ]
  },
  dropdownIcon: { class: 'w-2 h-2 text-[8px] leading-none' },
  clearIcon: { class: 'w-2 h-2 top-[19px] text-[8px] leading-none' },
  trigger: { class: 'px-1' },
  overlay: {
    style: {
      '--p-checkbox-checked-background-color': '#3F3F3F',
      '--p-checkbox-checked-border-color': '#3F3F3F'
    }
  },
  itemswrapper: { style: { maxHeight: 'none' } },
  panel: { class: '!max-h-none !rounded-b-xl !rounded-t-none rounded-2xl' },
  option: { class: 'whitespace-normal break-words' },
  wrapper: {
    class: [
      'w-[200px] absolute top-full left-0 !max-h-none !rounded-b-xl !rounded-t-none bg-[#fff] border border-solid border-t-0 border-[#BDBDBD]',
      props.loading ? 'pointer-events-none !bg-[#bdbdbd]' : ''
    ]
  },
  labelcontainer: {
    class:
      '!w-[160px] !min-w-[160px] whitespace-normal break-words overflow-visible flex flex-wrap grow h-auto min-h-[2rem]'
  },
  label: {
    class: 'flex items-center gap-1 [&>div]:m-0 [&>div]:p-0 [&>div]:shrink-0'
  },
  item: { class: ['flex items-center', props.loading ? '!bg-[#bdbdbd]' : ''] },
  token: {
    class: 'px-2 py-0 max-h-4 bg-[#EAEAEA] text-[13px] flex items-center'
  },
  tokenlabel: { class: 'h-4 pl-1 pr-3 relative top-[-2px]' },
  removetokenicon: {
    class: 'w-3 h-3',
    content: '×'
  },
  itemcheckbox: {
    class: 'items-center',
    root: { class: 'items-center' },
    box: {
      class:
        'w-4 h-4 items-center border-2 border-[#3F3F3F] rounded-[2px] data-[p-highlight=false]:!bg-[#fff] data-[p-highlight=true]:!bg-[#3F3F3F] data-[p-highlight=true]:border-[#3F3F3F]',
      style: {
        borderColor: '#3F3F3F !important',
        borderRadius: '2px !important',
        '--p-checkbox-checked-border-color': '#3F3F3F'
      }
    },
    icon: { class: 'text-white' }
  },
  header: { class: 'hidden' }
}))
</script>

<style scoped>
.p-multiselect.p-inputwrapper-focus[data-pc-section='root'] {
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  border-color: #bdbdbd;
}

.p-multiselect {
  border-bottom: 1px solid rgb(189, 189, 189) !important;
  border-radius: 12px !important;
  border-color: rgb(189, 189, 189) !important;
}

.p-overlay-open {
  border-bottom: none !important;
  border-radius: 12px 12px 0 0 !important;
}
</style>
